import * as THREE from "three";

import { deriveLoomAircraftOccurrenceRebuildDefinition,
  type LoomAircraftOccurrenceRebuildDefinition } from "../../api/loomRebuildV4";
import { loomAirfoilPoint } from "./loomAuthoringPreview";
import type {
  LoomAuthoringDocument,
  LoomFuselageStation,
  LoomPrimitiveDefinition,
  LoomVec3,
  LoomWingPanel,
} from "./loomAuthoringModel";
import {
  loomJunctionFairingRequestedContinuity,
  type LoomJunctionFairingIntent,
  type LoomJunctionFairingStation,
} from "./loomJunctionFairingIntent";
import { evaluateLoomPchip, prepareLoomPchip,
  type LoomPchipTable } from "./loomShapePreservingPchip";

export const LOOM_FAIRING_FOOTPRINT_PREVIEW_AUTHORITY =
  "visual-only-admitted-occurrence-fairing-intent-projection/v3" as const;

export type LoomFairingFootprintParent = "wing" | "fuselage";
export type LoomFairingFootprintSide = "port" | "starboard";

export interface LoomFairingFootprintCurve {
  readonly parent: LoomFairingFootprintParent;
  readonly points: readonly LoomVec3[];
  readonly side: LoomFairingFootprintSide;
  readonly tangentScales: readonly number[];
}

export type LoomJunctionFairingFootprintPreview = {
  readonly kind: "unavailable";
  readonly reason: string;
} | {
  readonly authority: typeof LOOM_FAIRING_FOOTPRINT_PREVIEW_AUTHORITY;
  readonly curves: readonly LoomFairingFootprintCurve[];
  readonly kind: "available";
  readonly nodeId: string;
  readonly parentSignatures: Readonly<Record<LoomFairingFootprintParent, string>>;
  readonly shapeSignature: string;
  readonly signature: string;
  readonly sourceRevision: number;
  readonly stationCount: number;
  readonly transitionId: string;
};

type FuselageDefinition = Extract<LoomPrimitiveDefinition, { primitive: "fuselage" }>;
type WingDefinition = Extract<LoomPrimitiveDefinition, { primitive: "wing" }>;

interface FuselageSection {
  readonly heightExponent: number;
  readonly heightM: number;
  readonly widthExponent: number;
  readonly widthM: number;
  readonly xM: number;
  readonly zCenterM: number;
}

interface SeamSample {
  readonly angleDeg: number;
  readonly point: THREE.Vector3;
  readonly upper: boolean;
  readonly u: number;
  readonly v: number;
}
interface ChartRates { readonly theta: number; readonly thetaRate: number; readonly xRate: number }
interface PreparedSeam {
  readonly center: THREE.Vector3;
  readonly samples: readonly (Omit<SeamSample, "angleDeg"> & { readonly rates: ChartRates | null })[];
}

type ProperRigid = LoomAircraftOccurrenceRebuildDefinition["occurrences"][number]["properRigid"];
type FuselageTables = Readonly<Record<Exclude<keyof FuselageSection, "xM">, LoomPchipTable>>;

const SAMPLE_COUNT = 32;
const BISECTION_STEPS = 28;
const FUSELAGE_TABLE_CACHE = new WeakMap<FuselageDefinition, FuselageTables>();
const SEAM_CACHE = new WeakMap<LoomAuthoringDocument, Map<string, PreparedSeam>>();

function stationSection(station: LoomFuselageStation): FuselageSection {
  return station.kind === "point"
    ? { xM: station.xM, widthM: 1e-6, heightM: 1e-6, widthExponent: 2,
      heightExponent: 2, zCenterM: station.zCenterM }
    : station;
}

function fuselageSection(definition: FuselageDefinition, xM: number): FuselageSection | null {
  let tables = FUSELAGE_TABLE_CACHE.get(definition);
  if (!tables) {
    const stations = [...definition.stations].sort((left, right) => left.xM - right.xM)
      .map(stationSection);
    const xs = stations.map(({ xM: x }) => x);
    const table = (key: Exclude<keyof FuselageSection, "xM">) => (
      prepareLoomPchip(xs, stations.map((station) => station[key]))
    );
    tables = {
      widthM: table("widthM"), heightM: table("heightM"),
      widthExponent: table("widthExponent"), heightExponent: table("heightExponent"),
      zCenterM: table("zCenterM"),
    };
    FUSELAGE_TABLE_CACHE.set(definition, tables);
  }
  const xs = tables.widthM.xs;
  if (xM < xs[0]! || xM > xs[xs.length - 1]!) return null;
  return {
    xM,
    widthM: evaluateLoomPchip(tables.widthM, xM),
    heightM: evaluateLoomPchip(tables.heightM, xM),
    widthExponent: evaluateLoomPchip(tables.widthExponent, xM),
    heightExponent: evaluateLoomPchip(tables.heightExponent, xM),
    zCenterM: evaluateLoomPchip(tables.zCenterM, xM),
  };
}

function fuselagePoint(definition: FuselageDefinition, xM: number, theta: number): THREE.Vector3 | null {
  const section = fuselageSection(definition, xM);
  if (!section || section.widthM <= 1e-9 || section.heightM <= 1e-9) return null;
  const cosine = Math.cos(theta), sine = Math.sin(theta);
  return new THREE.Vector3(
    xM,
    section.widthM * 0.5 * Math.sign(cosine) * Math.abs(cosine) ** (2 / section.widthExponent),
    section.zCenterM + section.heightM * 0.5 * Math.sign(sine)
      * Math.abs(sine) ** (2 / section.heightExponent),
  );
}

function fuselageResidual(definition: FuselageDefinition, point: THREE.Vector3): number {
  const section = fuselageSection(definition, point.x);
  if (!section || section.widthM <= 1e-9 || section.heightM <= 1e-9) return Number.POSITIVE_INFINITY;
  return Math.abs(point.y / (section.widthM * 0.5)) ** section.widthExponent
    + Math.abs((point.z - section.zCenterM) / (section.heightM * 0.5)) ** section.heightExponent - 1;
}

function wingPoint(panel: LoomWingPanel, u: number, upper: boolean, v: number): THREE.Vector3 {
  const span = panel.yTipM - panel.yRootM;
  const chord = THREE.MathUtils.lerp(panel.chordRootM, panel.chordTipM, v);
  const rootReferenceX = panel.xOffsetM + panel.sweepLocationRatio * panel.chordRootM;
  const referenceX = rootReferenceX
    + Math.tan(THREE.MathUtils.degToRad(panel.sweepDeg)) * span * v;
  const leadingX = referenceX - panel.sweepLocationRatio * chord;
  const y = panel.yRootM + span * v;
  const baseZ = panel.zOffsetM
    + Math.tan(THREE.MathUtils.degToRad(panel.dihedralDeg)) * span * v;
  const twist = THREE.MathUtils.degToRad(
    THREE.MathUtils.lerp(panel.twistRootDeg, panel.twistTipDeg, v),
  );
  const section = loomAirfoilPoint(panel.airfoilRoot, u, upper)
    .lerp(loomAirfoilPoint(panel.airfoilTip, u, upper), v);
  const localX = (section.x - panel.twistLocationRatio) * chord;
  const localZ = section.y * chord;
  return new THREE.Vector3(
    leadingX + panel.twistLocationRatio * chord
      + localX * Math.cos(twist) + localZ * Math.sin(twist),
    y,
    baseZ - localX * Math.sin(twist) + localZ * Math.cos(twist),
  );
}

function firstExteriorCrossing(
  panel: LoomWingPanel,
  fuselage: FuselageDefinition,
  u: number,
  upper: boolean,
): { point: THREE.Vector3; v: number } | null {
  let lower = 0;
  let lowerResidual = fuselageResidual(fuselage, wingPoint(panel, u, upper, lower));
  if (!Number.isFinite(lowerResidual) || lowerResidual > 1e-7) return null;
  let upperV = 1;
  let found = false;
  for (let index = 1; index <= 40; index += 1) {
    const candidate = index / 40;
    const residual = fuselageResidual(fuselage, wingPoint(panel, u, upper, candidate));
    if (lowerResidual <= 0 && residual >= 0) { upperV = candidate; found = true; break; }
    lower = candidate;
    lowerResidual = residual;
  }
  if (!found) return null;
  for (let iteration = 0; iteration < BISECTION_STEPS; iteration += 1) {
    const middle = 0.5 * (lower + upperV);
    if (fuselageResidual(fuselage, wingPoint(panel, u, upper, middle)) < 0) lower = middle;
    else upperV = middle;
  }
  const v = 0.5 * (lower + upperV);
  return { point: wingPoint(panel, u, upper, v), v };
}

type PeriodicFairingKey = "parentAOffsetM" | "parentBOffsetM"
  | "parentATangentScale" | "parentBTangentScale";

function periodicFairingValue(
  stations: readonly LoomJunctionFairingStation[],
  key: PeriodicFairingKey,
  angleDeg: number,
): number {
  const sorted = [...stations].sort((left, right) => left.junctionAngleDeg - right.junctionAngleDeg);
  const widths = sorted.map((station, index) => {
    const next = sorted[(index + 1) % sorted.length]!;
    return next.junctionAngleDeg + (index === sorted.length - 1 ? 360 : 0) - station.junctionAngleDeg;
  });
  const slopes = sorted.map((station, index) => (
    (sorted[(index + 1) % sorted.length]![key] - station[key]) / widths[index]!
  ));
  const derivatives = sorted.map((_station, index) => {
    const previous = (index + sorted.length - 1) % sorted.length;
    if (slopes[previous]! * slopes[index]! <= 0) return 0;
    const firstWeight = 2 * widths[index]! + widths[previous]!;
    const secondWeight = widths[index]! + 2 * widths[previous]!;
    return (firstWeight + secondWeight)
      / (firstWeight / slopes[previous]! + secondWeight / slopes[index]!);
  });
  const query = ((angleDeg % 360) + 360) % 360;
  let index = sorted.length - 1;
  for (let candidate = 0; candidate < sorted.length; candidate += 1) {
    if (sorted[candidate]!.junctionAngleDeg <= query) index = candidate;
    else break;
  }
  const nextIndex = (index + 1) % sorted.length;
  const x0 = sorted[index]!.junctionAngleDeg;
  const x1 = sorted[nextIndex]!.junctionAngleDeg + (nextIndex === 0 ? 360 : 0);
  const adjusted = query < x0 ? query + 360 : query;
  const width = x1 - x0;
  const t = (adjusted - x0) / width;
  const h00 = 2 * t ** 3 - 3 * t ** 2 + 1;
  const h10 = t ** 3 - 2 * t ** 2 + t;
  const h01 = -2 * t ** 3 + 3 * t ** 2;
  const h11 = t ** 3 - t ** 2;
  return h00 * sorted[index]![key] + h10 * width * derivatives[index]!
    + h01 * sorted[nextIndex]![key] + h11 * width * derivatives[nextIndex]!;
}

export function loomJunctionFairingPeriodicReach(
  stations: readonly LoomJunctionFairingStation[],
  key: "parentAOffsetM" | "parentBOffsetM",
  angleDeg: number,
): number {
  return periodicFairingValue(stations, key, angleDeg);
}

function wingContact(panel: LoomWingPanel, sample: SeamSample, reachM: number): THREE.Vector3 | null {
  if (reachM <= 0) return sample.point.clone();
  let lower = sample.v, upper = 1;
  const endpoint = wingPoint(panel, sample.u, sample.upper, upper);
  if (endpoint.distanceTo(sample.point) + 1e-12 < reachM) return null;
  for (let iteration = 0; iteration < BISECTION_STEPS; iteration += 1) {
    const middle = 0.5 * (lower + upper);
    if (wingPoint(panel, sample.u, sample.upper, middle).distanceTo(sample.point) < reachM) lower = middle;
    else upper = middle;
  }
  return wingPoint(panel, sample.u, sample.upper, 0.5 * (lower + upper));
}

function chartTheta(section: FuselageSection, point: THREE.Vector3): number {
  const cosine = Math.sign(point.y) * Math.abs(point.y / (section.widthM * 0.5)) ** (section.widthExponent / 2);
  const sine = Math.sign(point.z - section.zCenterM)
    * Math.abs((point.z - section.zCenterM) / (section.heightM * 0.5)) ** (section.heightExponent / 2);
  return Math.atan2(sine, cosine);
}

function chartRates(
  fuselage: FuselageDefinition,
  seam: Pick<SeamSample, "point">,
  seamTangent: THREE.Vector3,
  center: THREE.Vector3,
): ChartRates | null {
  const section = fuselageSection(fuselage, seam.point.x);
  if (!section || section.widthM <= 1e-9 || section.heightM <= 1e-9) return null;
  const theta = chartTheta(section, seam.point);
  const thetaAfter = fuselagePoint(fuselage, seam.point.x, theta + 1e-4);
  const thetaBefore = fuselagePoint(fuselage, seam.point.x, theta - 1e-4);
  const xAfter = fuselagePoint(fuselage, seam.point.x + 1e-4, theta);
  const xBefore = fuselagePoint(fuselage, seam.point.x - 1e-4, theta);
  if (!thetaAfter || !thetaBefore || !xAfter || !xBefore) return null;
  const dTheta = thetaAfter.sub(thetaBefore).multiplyScalar(5_000);
  const dX = xAfter.sub(xBefore).multiplyScalar(5_000);
  const normal = new THREE.Vector3().crossVectors(dTheta, dX).normalize();
  const direction = new THREE.Vector3().crossVectors(normal, seamTangent).normalize();
  const radial = new THREE.Vector3(seam.point.x - center.x, 0, seam.point.z - center.z);
  if (direction.dot(radial) < 0) direction.negate();
  const aa = dTheta.dot(dTheta), ab = dTheta.dot(dX), bb = dX.dot(dX);
  const ar = dTheta.dot(direction), br = dX.dot(direction), determinant = aa * bb - ab * ab;
  if (Math.abs(determinant) <= 1e-16) return null;
  let thetaRate = (ar * bb - br * ab) / determinant;
  let xRate = (br * aa - ar * ab) / determinant;
  const physicalRate = dTheta.clone().multiplyScalar(thetaRate).addScaledVector(dX, xRate).length();
  if (!Number.isFinite(physicalRate) || physicalRate <= 1e-12) return null;
  thetaRate /= physicalRate;
  xRate /= physicalRate;
  return { theta, thetaRate, xRate };
}

function fuselageContact(
  fuselage: FuselageDefinition,
  sample: SeamSample,
  rates: ChartRates | null,
  reachM: number,
): THREE.Vector3 | null {
  if (reachM <= 0) return sample.point.clone();
  if (!rates) return null;
  const evaluate = (distance: number) => fuselagePoint(
    fuselage,
    sample.point.x + distance * rates.xRate,
    rates.theta + distance * rates.thetaRate,
  );
  const xs = fuselage.stations.map(({ xM }) => xM);
  const xMin = Math.min(...xs), xMax = Math.max(...xs);
  let lower = 0, upper = Math.max(reachM * 2, 1e-6), bracketed = false;
  for (let expand = 0; expand < 8; expand += 1) {
    const candidateX = sample.point.x + upper * rates.xRate;
    if (candidateX <= xMin || candidateX >= xMax) return null;
    const point = evaluate(upper);
    if (!point) return null;
    if (point.distanceTo(sample.point) >= reachM) { bracketed = true; break; }
    upper *= 2;
  }
  if (!bracketed) return null;
  for (let iteration = 0; iteration < BISECTION_STEPS; iteration += 1) {
    const middle = 0.5 * (lower + upper);
    const point = evaluate(middle);
    if (!point) return null;
    if (point.distanceTo(sample.point) < reachM) lower = middle;
    else upper = middle;
  }
  return evaluate(0.5 * (lower + upper));
}

function hashPoints(label: string, curves: readonly (readonly (readonly number[])[])[]): string {
  let hash = 0x811c9dc5;
  const input = `${label}:${curves.flat(2).map((value) => Number(value).toFixed(5)).join(",")}`;
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return `${label}:${(hash >>> 0).toString(16).padStart(8, "0")}`;
}

function axisVector(axis: "+x" | "-x" | "+y" | "-y" | "+z" | "-z"): THREE.Vector3 {
  const sign = axis[0] === "+" ? 1 : -1;
  return new THREE.Vector3(axis[1] === "x" ? sign : 0, axis[1] === "y" ? sign : 0,
    axis[1] === "z" ? sign : 0);
}

function closedCurveCentroid(samples: readonly Omit<SeamSample, "angleDeg">[]): THREE.Vector3 {
  const center = new THREE.Vector3();
  let totalLength = 0;
  samples.forEach((sample, index) => {
    const following = samples[(index + 1) % samples.length]!.point;
    const length = sample.point.distanceTo(following);
    center.addScaledVector(sample.point.clone().add(following), 0.5 * length);
    totalLength += length;
  });
  return center.multiplyScalar(1 / totalLength);
}

function junctionAngleDeg(
  point: THREE.Vector3,
  center: THREE.Vector3,
  intent: LoomJunctionFairingIntent,
): number {
  const normal = axisVector(intent.construction.angleFrame.normalAxis);
  const zero = axisVector(intent.construction.angleFrame.zeroAxis);
  const positive = new THREE.Vector3().crossVectors(normal, zero).normalize();
  const radial = point.clone().sub(center);
  return (THREE.MathUtils.radToDeg(Math.atan2(radial.dot(positive), radial.dot(zero))) + 360) % 360;
}

function poseMatrix(pose: ProperRigid): THREE.Matrix4 {
  return new THREE.Matrix4().compose(
    new THREE.Vector3().fromArray(pose.translationM),
    new THREE.Quaternion().fromArray(pose.rotationQuaternionXyzw),
    new THREE.Vector3(1, 1, 1),
  );
}

function tuples(points: readonly THREE.Vector3[], pose: THREE.Matrix4, mirrorY = false): LoomVec3[] {
  return points.map((source) => {
    const point = source.clone();
    if (mirrorY) point.y *= -1;
    point.applyMatrix4(pose);
    return point.toArray() as unknown as LoomVec3;
  });
}

export function loomJunctionFairingFootprintIntentSignature(intent: LoomJunctionFairingIntent): string {
  return intent.construction.stations.map((station) => [
    station.stationId,
    station.junctionAngleDeg.toFixed(3),
    station.parentAOffsetM.toFixed(6),
    station.parentBOffsetM.toFixed(6),
  ].join(":" )).join("|");
}

export function loomJunctionFairingIntentPreviewSignature(intent: LoomJunctionFairingIntent): string {
  const continuity = loomJunctionFairingRequestedContinuity(intent);
  return `${intent.schema}:${intent.construction.stations.map((station) => [
    station.stationId, station.junctionAngleDeg, station.parentAOffsetM, station.parentBOffsetM,
    station.parentATangentScale, station.parentBTangentScale,
  ].join(":" )).join("|")}:${continuity.parentA}:${continuity.parentB}`;
}

export function deriveLoomJunctionFairingFootprintPreview(
  document: LoomAuthoringDocument,
  nodeId: string,
  intent: LoomJunctionFairingIntent,
): LoomJunctionFairingFootprintPreview {
  const admitted = deriveLoomAircraftOccurrenceRebuildDefinition(document);
  const source = admitted?.sourceDefinitions[0]?.definition;
  const properWing = admitted?.occurrences.find((row) => row.role === "wing" && row.parity === "proper");
  const reflectedWing = admitted?.occurrences.find((row) => row.role === "wing" && row.parity === "reflected");
  const fuselageOccurrence = admitted?.occurrences.find((row) => row.role === "fuselage");
  const wingSourceId = source?.wing.sourceNodeId;
  const fuselageSourceId = source?.fuselage.sourceNodeId;
  const wingNode = document.nodes.find((node) => node.id === wingSourceId);
  const fuselageNode = document.nodes.find((node) => node.id === fuselageSourceId);
  if (!admitted || source?.schema !== "loom.wing-body-definition/v1" || nodeId !== wingSourceId
      || !properWing || !reflectedWing || !fuselageOccurrence
      || wingNode?.type !== "primitive" || wingNode.definition.primitive !== "wing"
      || fuselageNode?.type !== "primitive" || fuselageNode.definition.primitive !== "fuselage") {
    return { kind: "unavailable", reason: "The selected fairing has no admitted projectable wing/fuselage occurrence parents." };
  }
  const wing: WingDefinition = wingNode.definition;
  const fuselage: FuselageDefinition = fuselageNode.definition;
  const panel = [...wing.panels]
    .filter((candidate) => Math.max(candidate.yRootM, candidate.yTipM) > 0)
    .sort((left, right) => Math.min(Math.abs(left.yRootM), Math.abs(left.yTipM))
      - Math.min(Math.abs(right.yRootM), Math.abs(right.yTipM)))[0];
  if (!panel) return { kind: "unavailable", reason: "The selected wing has no starboard root panel." };
  let prepared = SEAM_CACHE.get(document)?.get(nodeId);
  if (!prepared) {
    const sourceSamples: Array<Omit<SeamSample, "angleDeg">> = [];
    const add = (u: number, upper: boolean) => {
      const crossing = firstExteriorCrossing(panel, fuselage, u, upper);
      if (crossing) sourceSamples.push({ ...crossing, u, upper });
    };
    for (let index = SAMPLE_COUNT - 1; index >= 0; index -= 1) add(index / (SAMPLE_COUNT - 1), true);
    for (let index = 1; index < SAMPLE_COUNT - 1; index += 1) add(index / (SAMPLE_COUNT - 1), false);
    if (sourceSamples.length < 48) {
      return { kind: "unavailable", reason: "The admitted parents do not form a projectable closed root seam." };
    }
    const center = closedCurveCentroid(sourceSamples);
    prepared = { center, samples: sourceSamples.map((sample, index) => {
      const before = sourceSamples[(index + sourceSamples.length - 1) % sourceSamples.length]!.point;
      const after = sourceSamples[(index + 1) % sourceSamples.length]!.point;
      return { ...sample, rates: chartRates(fuselage, sample,
        after.clone().sub(before).normalize(), center) };
    }) };
    const documentCache = SEAM_CACHE.get(document) ?? new Map<string, PreparedSeam>();
    documentCache.set(nodeId, prepared);
    SEAM_CACHE.set(document, documentCache);
  }
  const seam = prepared.samples.map((sample) => ({
    ...sample,
    angleDeg: junctionAngleDeg(sample.point, prepared.center, intent),
  }));
  const wingPoints = seam.map((sample) => wingContact(panel, sample,
    loomJunctionFairingPeriodicReach(intent.construction.stations, "parentAOffsetM", sample.angleDeg)));
  if (!wingPoints.every((point): point is THREE.Vector3 => point !== null)) {
    return { kind: "unavailable", reason: "The requested wing reach leaves the admitted parent chart." };
  }
  const fuselagePoints = seam.map((sample) => fuselageContact(fuselage, sample, sample.rates,
    loomJunctionFairingPeriodicReach(intent.construction.stations, "parentBOffsetM", sample.angleDeg)));
  if (!fuselagePoints.every((point): point is THREE.Vector3 => point !== null)) {
    return { kind: "unavailable", reason: "The requested fuselage reach leaves the admitted parent chart." };
  }
  const pose = poseMatrix(properWing.properRigid);
  const wingStarboard = tuples(wingPoints, pose), fuselageStarboard = tuples(fuselagePoints, pose);
  const wingCurves = [wingStarboard, tuples(wingPoints, pose, true)];
  const fuselageCurves = [fuselageStarboard, tuples(fuselagePoints, pose, true)];
  const wingTangentScales = seam.map((sample) => periodicFairingValue(
    intent.construction.stations, "parentATangentScale", sample.angleDeg,
  ));
  const fuselageTangentScales = seam.map((sample) => periodicFairingValue(
    intent.construction.stations, "parentBTangentScale", sample.angleDeg,
  ));
  const curves: LoomFairingFootprintCurve[] = [
    { parent: "wing", side: "starboard", points: wingStarboard, tangentScales: wingTangentScales },
    { parent: "fuselage", side: "starboard", points: fuselageStarboard, tangentScales: fuselageTangentScales },
    { parent: "wing", side: "port", points: wingCurves[1]!, tangentScales: wingTangentScales },
    { parent: "fuselage", side: "port", points: fuselageCurves[1]!, tangentScales: fuselageTangentScales },
  ];
  const parentSignatures = {
    wing: hashPoints("wing", wingCurves),
    fuselage: hashPoints("fuselage", fuselageCurves),
  };
  const shapeSignature = hashPoints("shape", curves.map(({ points, tangentScales }) => (
    points.map((point, index) => [...point, tangentScales[index]!])
  )));
  return {
    kind: "available",
    authority: LOOM_FAIRING_FOOTPRINT_PREVIEW_AUTHORITY,
    curves,
    nodeId,
    parentSignatures,
    shapeSignature,
    signature: `${document.revision}:${parentSignatures.wing}:${parentSignatures.fuselage}:${shapeSignature}`,
    sourceRevision: document.revision,
    stationCount: intent.construction.stations.length,
    transitionId: intent.transitionId,
  };
}
